(() => {
  if (window.AhelWishlist) {
    window.AhelWishlist.refresh();
    return;
  }

  const storageKey = 'ahel-wishlist-v1';
  const maxItems = 100;
  const productRequestCache = new Map();
  let itemsCache;
  let renderVersion = 0;

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const normaliseItems = value => {
    const source = Array.isArray(value) ? value : value?.items;
    if (!Array.isArray(source)) return [];

    const knownHandles = new Set();
    return source
      .map(item => {
        const handle = typeof item === 'string' ? item : item?.handle;
        if (typeof handle !== 'string' || handle.trim() === '') return null;

        return {
          handle: handle.trim(),
          addedAt: Number(item?.addedAt) || 0
        };
      })
      .filter(item => {
        if (!item || knownHandles.has(item.handle)) return false;
        knownHandles.add(item.handle);
        return true;
      })
      .sort((first, second) => second.addedAt - first.addedAt)
      .slice(0, maxItems);
  };

  const getItems = () => {
    if (itemsCache) return itemsCache;

    try {
      itemsCache = normaliseItems(JSON.parse(window.localStorage.getItem(storageKey) || '[]'));
    } catch (error) {
      itemsCache = [];
    }

    return itemsCache;
  };

  const commitItems = items => {
    itemsCache = normaliseItems(items);

    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ version: 1, items: itemsCache }));
    } catch (error) {}

    window.dispatchEvent(new CustomEvent('ahel:wishlist:change', { detail: { items: itemsCache } }));
  };

  const getCountLabel = count => {
    const template = document.querySelector('[data-wishlist-count-template]')?.dataset.wishlistCountTemplate;
    return template ? template.replace('[count]', count) : `${count}`;
  };

  const updateControls = items => {
    const handles = new Set(items.map(item => item.handle));
    const count = items.length;
    const displayCount = count > 99 ? '+' : count;

    document.querySelectorAll('[data-wishlist-toggle]').forEach(control => {
      const active = handles.has(control.dataset.wishlistHandle);
      const label = active ? control.dataset.wishlistRemoveLabel : control.dataset.wishlistAddLabel;
      const ariaLabel = active ? control.dataset.wishlistRemoveAria : control.dataset.wishlistAddAria;

      control.classList.toggle('is-active', active);
      control.setAttribute('aria-pressed', String(active));
      control.setAttribute('aria-label', ariaLabel || label || '');
      control.setAttribute('title', ariaLabel || label || '');
      control.querySelector('[data-wishlist-label]')?.replaceChildren(label || '');
    });

    document.querySelectorAll('[data-wishlist-count]').forEach(counter => {
      counter.textContent = displayCount;
      counter.hidden = count === 0;
    });

    document.querySelectorAll('[data-wishlist-count-status]').forEach(status => {
      status.textContent = getCountLabel(count);
    });

    document.querySelectorAll('[data-wishlist-open]').forEach(opener => {
      opener.setAttribute('aria-expanded', String(document.querySelector('wishlist-drawer')?.classList.contains('is-open') || false));
    });
  };

  const updateSurfaceState = (surface, items) => {
    const isEmpty = items.length === 0;
    const emptyState = surface.querySelector('[data-wishlist-empty]');
    const viewAll = surface.querySelector('[data-wishlist-view-all]');

    surface.dataset.wishlistState = isEmpty ? 'empty' : 'populated';
    emptyState?.toggleAttribute('hidden', !isEmpty);
    emptyState?.classList.toggle('hidden', !isEmpty);
    emptyState?.setAttribute('aria-hidden', String(!isEmpty));
    viewAll?.toggleAttribute('hidden', isEmpty);
    viewAll?.classList.toggle('hidden', isEmpty);
  };

  const setMessage = (surface, message = '') => {
    const element = surface.querySelector('[data-wishlist-message]');
    if (!element) return;

    element.textContent = message;
    element.hidden = message === '';
  };

  const loadProductCard = handle => {
    if (productRequestCache.has(handle)) return productRequestCache.get(handle);

    const request = fetch(`${rootUrl()}products/${encodeURIComponent(handle)}?view=card`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async response => {
        if (response.status === 404 || response.status === 410) {
          return { handle, missing: true };
        }

        if (!response.ok) return { handle, error: true };

        const html = await response.text();
        if (!html.includes('product-card')) return { handle, missing: true };

        return { handle, html };
      })
      .catch(() => ({ handle, error: true }));

    productRequestCache.set(handle, request);
    window.setTimeout(() => productRequestCache.delete(handle), 5000);
    return request;
  };

  const renderTarget = async (target, items, version) => {
    const surface = target.closest('[data-wishlist-surface]');
    if (!surface) return;

    const limit = Number(target.dataset.wishlistLimit || 0);
    const targetItems = limit > 0 ? items.slice(0, limit) : items;
    updateSurfaceState(surface, items);

    if (targetItems.length === 0) {
      target.replaceChildren();
      target.removeAttribute('aria-busy');
      setMessage(surface);
      return;
    }

    target.setAttribute('aria-busy', 'true');
    const results = await Promise.all(targetItems.map(item => loadProductCard(item.handle)));
    if (version !== renderVersion) return;

    const missingHandles = results.filter(result => result.missing).map(result => result.handle);
    if (missingHandles.length > 0) {
      commitItems(items.filter(item => !missingHandles.includes(item.handle)));
      return;
    }

    const cards = results.filter(result => result.html).map(result => result.html);
    const hasError = results.some(result => result.error);
    target.innerHTML = cards.join('');
    target.removeAttribute('aria-busy');
    setMessage(surface, hasError ? target.dataset.wishlistError || '' : '');
    updateControls(items);
  };

  const render = (items = getItems()) => {
    renderVersion += 1;
    const version = renderVersion;
    updateControls(items);
    document.querySelectorAll('[data-wishlist-target]').forEach(target => {
      renderTarget(target, items, version);
    });
  };

  const toggle = handle => {
    if (!handle) return;

    const items = getItems();
    const index = items.findIndex(item => item.handle === handle);
    if (index >= 0) {
      commitItems(items.filter(item => item.handle !== handle));
      return;
    }

    commitItems([{ handle, addedAt: Date.now() }, ...items]);
  };

  const hasAnotherOpenOverlay = () => Boolean(
    document.querySelector('quick-cart-drawer.is--open, shop-the-look-drawer.is--open, cart-drawer.is-open, cart-drawer.active, .drawer__container.menu-opening, .drawer__container[open]')
  );

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      if (this.handleKeydown) return;

      this.handleKeydown = event => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();

        if (event.key !== 'Tab' || !this.classList.contains('is-open')) return;

        const focusableElements = Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
          .filter(element => !element.hidden && element.getClientRects().length > 0);
        if (focusableElements.length === 0) return;

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      };
      this.handleClick = event => {
        if (event.target.closest('[data-wishlist-close]')) this.close();
      };
      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleClick);
      this.handleKeydown = undefined;
      this.handleClick = undefined;
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;

      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.querySelectorAll('[data-wishlist-open]').forEach(button => button.setAttribute('aria-expanded', 'true'));
      render();
      window.requestAnimationFrame(() => this.querySelector('[data-wishlist-close]')?.focus());
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[data-wishlist-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (!hasAnotherOpenOverlay()) document.body.classList.remove('overflow-hidden');
      window.requestAnimationFrame(() => this.opener?.focus?.());
    }
  }

  if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-wishlist-open]');
    if (opener) {
      event.preventDefault();
      document.querySelector('wishlist-drawer')?.open(opener);
      return;
    }

    const control = event.target.closest('[data-wishlist-toggle]');
    if (!control) return;

    event.preventDefault();
    toggle(control.dataset.wishlistHandle);
  });

  window.addEventListener('ahel:wishlist:change', event => render(event.detail.items));
  window.addEventListener('storage', event => {
    if (event.key !== storageKey) return;
    itemsCache = undefined;
    render();
  });

  window.AhelWishlist = {
    getItems: () => [...getItems()],
    refresh: () => render(),
    toggle
  };

  render();
})();
